import type { CampaignCreationCapabilities } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import type { SectionId } from "@/components/campaign-creation/wizard/wizard-navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AUTO_TARGET_GROUPS, type CampaignCreationFormValues } from "@/lib/campaign-creation-state";

export function CampaignCreationReview({
  shopName,
  values,
  capabilities,
  onEdit,
}: {
  shopName: string;
  values: CampaignCreationFormValues;
  capabilities: CampaignCreationCapabilities;
  onEdit: (step: 1 | 2, section?: SectionId) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const grouped = new Map<string, typeof values.products>();
  for (const product of values.products)
    grouped.set(product.variationGroupKey, [
      ...(grouped.get(product.variationGroupKey) ?? []),
      product,
    ]);
  const summaries = [
    {
      title: t("review.profileType"),
      edit: () => onEdit(1),
      content: (
        <ReviewRows
          rows={[
            [t("profile.label"), shopName],
            [t("review.type"), t("campaignType.sp.title")],
          ]}
        />
      ),
    },
    {
      title: t("review.campaign"),
      edit: () => onEdit(2, "campaign"),
      content: (
        <ReviewRows
          rows={[
            [t("campaign.name"), values.campaignName],
            [t("campaign.startDate"), values.startDate],
            [t("campaign.endDate"), values.endDate || t("review.noEndDate")],
            [
              t("campaign.targeting"),
              values.targetingMode === "manual"
                ? t("campaign.targetingOptions.manual")
                : t("campaign.targetingOptions.automatic"),
            ],
            [
              t("campaign.dailyBudget", { currency: capabilities.currencyCode }),
              values.dailyBudget,
            ],
            ...(capabilities.features.offAmazonSettings === "writable"
              ? [
                  [
                    t("campaign.offsiteStrategy"),
                    values.offsiteStrategy === "increase_reach"
                      ? t("campaign.offsiteOptions.increaseReach")
                      : t("campaign.offsiteOptions.limitSpend"),
                  ],
                ]
              : []),
          ]}
        />
      ),
    },
    {
      title: t("review.bidding"),
      edit: () => onEdit(2, "bidding"),
      content: (
        <ReviewRows
          rows={[
            [t("campaign.strategy"), t(`campaign.strategies.${values.biddingStrategy}`)],
            ...Object.entries(values.placementAdjustments).map(([placement, percentage]) => [
              t(`placements.${placement}`),
              `${percentage}%`,
            ]),
          ]}
        />
      ),
    },
    {
      title: t("review.adGroupProducts"),
      edit: () => onEdit(2, "products"),
      content: (
        <div className="flex flex-col gap-3">
          <ReviewRows
            rows={[
              [t("campaign.adGroupName"), values.adGroupName],
              [
                t("products.added", {
                  count: values.products.length,
                  max: capabilities.limits.maxProducts,
                }),
                String(values.products.length),
              ],
            ]}
          />
          <div className="max-h-64 overflow-y-auto rounded-lg border border-line">
            {[...grouped.entries()].map(([group, products]) => (
              <div key={group} className="border-b border-line p-3 last:border-b-0">
                <p className="font-mono text-[11px] font-medium">{group}</p>
                {products.map((product) => (
                  <p key={product.sku} className="mt-1 font-mono text-[10px] text-muted-foreground">
                    {product.sku} · {product.asin}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title:
        values.targetingMode === "manual" ? t("manual.targetingType") : t("review.automaticBids"),
      edit: () => onEdit(2, "automaticBids"),
      content:
        values.targetingMode === "manual" ? (
          <div className="flex flex-col gap-3">
            <ReviewRows
              rows={[
                [
                  t("manual.targetingType"),
                  values.manualTargetingType === "keyword"
                    ? t("manual.keyword.title")
                    : t("manual.product.title"),
                ],
                [t("manual.defaultBid"), values.manualDefaultBid],
              ]}
            />
            <ReviewList
              title={
                values.manualTargetingType === "keyword"
                  ? t("manual.keyword.editorTitle")
                  : t("manual.product.editorTitle")
              }
              rows={
                values.manualTargetingType === "keyword"
                  ? values.manualKeywords.map(
                      (target) =>
                        `${target.text} · ${t(`manual.matches.${target.matchType}`)} · ${target.bid}`,
                    )
                  : values.manualProducts.map((target) => `${target.asin} · ${target.bid}`)
              }
            />
          </div>
        ) : values.bidMode === "default" ? (
          <ReviewRows rows={[[t("bids.default"), values.defaultBid]]} />
        ) : (
          <ReviewRows
            rows={AUTO_TARGET_GROUPS.map((group) => [
              t(`bids.groups.${group}`, { currency: capabilities.currencyCode }),
              values.groupEnabled[group] ? values.groupBids[group] : t("bids.disabled"),
            ])}
          />
        ),
    },
    {
      title: t("review.negatives"),
      edit: () => onEdit(2, "negatives"),
      content: (
        <div className="grid grid-cols-2 gap-4">
          <ReviewList
            title={t("negatives.keywords.title")}
            rows={values.negativeKeywords.map(
              (item) => `${item.text} · ${t(`negatives.match.${item.matchType}`)}`,
            )}
          />
          <ReviewList title={t("negatives.products.title")} rows={values.negativeProducts} />
        </div>
      ),
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-4">
      {summaries.map((summary) => (
        <Card key={summary.title}>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>{summary.title}</CardTitle>
            <Button type="button" variant="ghost" size="xs" onClick={summary.edit}>
              {t("actions.edit")}
            </Button>
          </CardHeader>
          <CardContent>{summary.content}</CardContent>
        </Card>
      ))}
    </div>
  );
}

function ReviewRows({ rows }: { rows: string[][] }) {
  return (
    <dl className="flex flex-col gap-2">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-start justify-between gap-4 text-xs">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="max-w-[65%] text-right font-medium">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function ReviewList({ title, rows }: { title: string; rows: string[] }) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div>
      <h4 className="mb-2 text-xs font-medium">{title}</h4>
      {rows.length ? (
        <div className="max-h-44 overflow-y-auto rounded-lg bg-subtle p-2">
          {rows.map((row) => (
            <p key={row} className="py-1 text-[11px]">
              {row}
            </p>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("review.none")}</p>
      )}
    </div>
  );
}
